import { DateTime } from "luxon";

const initialState = {
  user: null,
  notaryJobs: null,
  notaryRequests: [],
  lastWebSocketMessage: null,
  navigateToRealRequestsTab: false,
  requestCountdowns: {}, // Store countdown timers for each request
};

const notarySessionReducer = (state = initialState, action) => {
  switch (action.type) {
    case "DISMISS_RON_REQUEST":
      const filteredRequests = state.notaryRequests.filter(
        (request) => request.id !== action.payload
      );
      return {
        ...state,
        notaryRequests: filteredRequests,
      };
    case "DISMISS_ALL_RON_REQUEST":
      return {
        ...state,
        notaryRequests: [],
        requestCountdowns: {},
      };
    case "NAVIGATE_TO_REAL_REQUESTS_TAB":
      return {
        ...state,
        navigateToRealRequestsTab: true,
      };
    case "CLEAR_NAVIGATE_TO_REAL_REQUESTS_TAB":
      return {
        ...state,
        navigateToRealRequestsTab: false,
      };
    case "ADD_REAL_TIME_REQUEST":
      // Check if a request with the same ID already exists
      const existingRequestIndex = state.notaryRequests.findIndex(
        (request) => request.id === action.payload.id
      );

      if (existingRequestIndex !== -1) {
        // Replace the existing request with the new one (most up-to-date)
        const updatedRequests = [...state.notaryRequests];
        updatedRequests[existingRequestIndex] = action.payload;
        return {
          ...state,
          notaryRequests: updatedRequests,
        };
      } else {
        // Add new request if no duplicate ID found
        const newRequest = action.payload;
        const countdownTime = 5 * 60; // 5 minutes in seconds

        return {
          ...state,
          notaryRequests: [...state.notaryRequests, newRequest],
          requestCountdowns: {
            ...state.requestCountdowns,
            [newRequest.id]: countdownTime,
          },
        };
      }
    case "UPDATE_REQUEST_COUNTDOWN":
      const { requestId, timeRemaining } = action.payload;
      return {
        ...state,
        requestCountdowns: {
          ...state.requestCountdowns,
          [requestId]: timeRemaining,
        },
      };
    case "REMOVE_EXPIRED_REQUEST":
      const expiredRequestId = action.payload;
      const updatedCountdowns = { ...state.requestCountdowns };
      delete updatedCountdowns[expiredRequestId];
      return {
        ...state,
        notaryRequests: state.notaryRequests.filter(
          (request) => request.id !== expiredRequestId
        ),
        requestCountdowns: updatedCountdowns,
      };
    case "ADD_NOTARY_USER_DATA":
      return {
        ...state,
        user: action.payload.data,
        notaryJobs: action.payload.jobs,
      };
    case "UPDATE_NOTARY_USER_DATA":
      return {
        ...state,
        user: action.payload.user,
        notaryJobs: action.payload.jobs,
      };
    case "UPDATE_NOTARY_JOBS":
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          jobs_list: action.payload.jobs_list,
          accepted_job_list: action.payload.accepted_job_list,
        },
      };
    case "ADD_JOB_TO_QUEUE": //! BROADCAST
      const job = action.payload;
      // Assuming `ronDateRaw` always contains at least one element and the required fields.
      const rawDate = job.ronDateRaw[0];

      // Create a UTC DateTime object from the raw date information
      const utcDate = DateTime.utc(
        rawDate.year,
        rawDate.month,
        rawDate.day_of_month,
        parseInt(rawDate.time.split(":")[0]),
        parseInt(rawDate.time.split(":")[1])
      );

      // Get the user's timezone from the state or default to America/New_York if not available
      const userTimezone = state.user?.timezone || "America/New_York";

      // Convert UTC DateTime to the user's timezone using the `signee_timezone` field
      const localDate = utcDate.setZone(userTimezone);

      // Format the localDate to the desired string format "April 22nd 2024, 10:30 PM"
      const formattedDate = localDate.toFormat("MMMM dd'th' yyyy, h:mm a");

      // Update the job object with the new date string
      const updatedJob = {
        ...job,
        ronDateString: formattedDate,
      };

      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          jobs_list: [updatedJob, ...state.notaryJobs.jobs_list],
        },
      };
    case "REMOVE_JOB_FROM_QUEUE": //! BROADCAST
      const jobIdToRemove = action.payload.id;
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          // Filter out the job that matches the ID to remove
          jobs_list: state.notaryJobs.jobs_list.filter(
            (job) => job.id !== jobIdToRemove
          ),
        },
      };
    case "ADD_ACCEPTED_QUEUE_JOB": //! PERSONAL
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          accepted_job_list: [
            action.payload,
            ...state.notaryJobs.accepted_job_list,
          ],
        },
      };
    case "REMOVE_ACCEPTED_QUEUE_JOB": //! PERSONAL
      const acceptedIdToRemove = action.payload.id; // Assuming each job has a unique 'id' field
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          // Filter out the job that matches the ID to remove
          accepted_job_list: state.notaryJobs.accepted_job_list.filter(
            (job) => job.id !== acceptedIdToRemove
          ),
        },
      };
    case "STORE_WEBSOCKET_MESSAGE":
      return {
        ...state,
        lastWebSocketMessage: action.payload,
      };
    //! //////////////////////////////////////////////////////////////////////////////////
    default:
      return state;
  }
};

export default notarySessionReducer;
